import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Form, Row, Col, Button } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { VALID_TRANSPORT_METHODS, VERIFICATION_USED_IN } from "../../../data";
import {
  requestAccountCreationToken,
  requestVerifySecondaryChannelToken,
  verifyAccountCreationRequest,
  verifySecondaryChannel,
} from "../../../services";
import { replaceCountryCodeInMobileNumber } from "../../../utils";
import PnSLoadingSpinner from "../pnsLoadingSpinner/PnSLoadingSpinner";
import VerificationInput from "./verificationInput/VerificationInput";

import "./Verification.css";

const onMissingFunctionRef = () => {
  console.error("Missing function reference!");
  toast.error(
    <div>
      Invalid action!
      <br />
      <small>If the issue persists, please contact P&S.</small>
    </div>
  );
};

const Verification = ({
  usedInSignup,
  mobileNumber,
  isVerficationProcessOnGoing,
  referenceToken,
  accountVerifyToken,
  disableResend,
  emailReferenceToken,
  transport,
  text,
  verificationText,
  otherData,
  verifiedSecondaryChannelToken,
  verifiedEmail,
  setIsChildLoading,
  setMobileNumberSendOtpResponse,
  setAccountVerifyToken,
  setEmailSendOtpResponse,
  setIsMobileNumberVerified,
  setIsEmailVerified,
  callbackAfterSuccessfulVerify,
  setAccountInfo,
  setIsResedingOtp,
}) => {
  const [verifyTransportData, setVerifyTransportData] = useState({
    transport: "",
    text: "",
    verificationText: null,
    otherData: {},
  });
  const [codeItem, setCodeItem] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [usedIn, setUsedIn] = useState("");
  const isResedingOtp = useMemo(() => false, []);
  const location = useLocation();

  const onResendCode = useCallback(async () => {
    try {
      setIsChildLoading(true);
      setIsResedingOtp(true);

      if (verifyTransportData.transport === VALID_TRANSPORT_METHODS.EMAIL) {
        const {
          secondaryChannelVerifyToken: newSecondaryChannelVerifyToken,
          nextEligibleTimestamp,
          waitSeconds,
        } = await requestVerifySecondaryChannelToken({
          email: verifyTransportData.otherData?.email,
          ...(verifyTransportData.otherData?.memberId
            ? { memberId: verifyTransportData.otherData.memberId }
            : {}),
        });
        setEmailSendOtpResponse(newSecondaryChannelVerifyToken);

        return { nextEligibleTimestamp, waitSeconds };
      } else {
        const {
          accountVerifyToken: newAccountVerifyToken,
          nextEligibleTimestamp,
          waitSeconds,
        } = await requestAccountCreationToken({
          mobileNumber,
        });
        setAccountVerifyToken(newAccountVerifyToken);

        return { nextEligibleTimestamp, waitSeconds };
      }
    } catch (e) {
      console.error(e);
      toast.error(
        <div>
          Failed to resend verification code!
          <br />
          {e.error || e.message
            ? `Error: ${e.error || e.message}`
            : "Please try again later."}
          <br />
          <small>If the issue persists, please contact P&S.</small>
        </div>
      );
      return {};
    } finally {
      setIsChildLoading(false);
      setIsResedingOtp(false);
    }
  }, [
    verifyTransportData,
    mobileNumber,
    setIsChildLoading,
    setEmailSendOtpResponse,
    setAccountVerifyToken,
    setIsResedingOtp,
  ]);

  const onSubmit = useCallback(
    async (event) => {
      try {
        event.preventDefault();
        event.stopPropagation();
        if (codeItem.length === 5) {
          if (accountVerifyToken) {
            if (emailReferenceToken && !verifiedSecondaryChannelToken) {
              const verifyTransportPayload = {
                secondaryChannelVerifyToken: emailReferenceToken,
                otpCode: codeItem,
                channel: VALID_TRANSPORT_METHODS.EMAIL,
                channelToVerify: verifyTransportData.otherData?.email,
              };
              setIsChildLoading(true);
              setIsVerifying(true);

              const { verifiedSecondaryChannelToken } =
                await verifySecondaryChannel(verifyTransportPayload);
              setIsEmailVerified(
                true,
                verifyTransportData.otherData?.email,
                verifiedSecondaryChannelToken
              );
            } else {
              const verifyAccountCreationRequestPayload = {
                accountVerifyToken,
                otpCode: codeItem,
                channel: VALID_TRANSPORT_METHODS.MOBILE,
                channelValue: mobileNumber,
              };

              setIsChildLoading(true);
              setIsVerifying(true);
              const verifiedResponse = await verifyAccountCreationRequest(
                verifyAccountCreationRequestPayload
              );
              const { accountCreateToken, ...restOfResponse } =
                verifiedResponse ?? {};

              setAccountInfo({
                accountCreateToken: accountCreateToken || null,
                data: {
                  firstName: restOfResponse?.firstName || "",
                  lastName: restOfResponse?.lastName || "",
                  mobileNumber: restOfResponse?.mobileNumber || mobileNumber,
                  email: restOfResponse?.email || "",
                  dob: restOfResponse?.birthDate || "",
                  gender: restOfResponse?.gender || "",
                  nearestStore: restOfResponse?.merchantLocationId || "",
                  preferredChannel:
                    restOfResponse?.notificationPreference?.preferredChannel ||
                    "",
                  allowPromotionalNotifications:
                    !!restOfResponse?.notificationPreference
                      ?.allowPromotionalNotifications,
                  ...(restOfResponse?.residentialAddress
                    ? { ...restOfResponse.residentialAddress }
                    : {}),
                  restOfMember: { ...restOfResponse },
                },
                memberExists: !!restOfResponse?.memberId,
              });
              setIsMobileNumberVerified(true);
            }
          } else {
            const verifyTransportPayload = {
              secondaryChannelVerifyToken: emailReferenceToken,
              otpCode: codeItem,
              channel: VALID_TRANSPORT_METHODS.EMAIL,
              channelToVerify: verifyTransportData.otherData?.email,
            };
            setIsChildLoading(true);
            setIsVerifying(true);

            const { verifiedSecondaryChannelToken } =
              await verifySecondaryChannel(verifyTransportPayload);
            setIsEmailVerified(
              true,
              verifyTransportData.otherData?.email,
              verifiedSecondaryChannelToken
            );
          }

          setIsChildLoading(false);
          setIsVerifying(false);
          callbackAfterSuccessfulVerify();
        }
      } catch (e) {
        setIsChildLoading(false);
        setIsVerifying(false);
        console.error(e);
        toast.error(
          <div>
            Failed to verify
            {verifyTransportData.text ? " " + verifyTransportData.text : ""}!
            <br />
            {e.error || e.message
              ? `Error: ${e.error || e.message}`
              : "Please try again later."}
            <br />
            <small>If the issue persists, please contact P&S.</small>
          </div>
        );
      } finally {
      }
    },
    [
      codeItem,
      accountVerifyToken,
      setIsChildLoading,
      callbackAfterSuccessfulVerify,
      emailReferenceToken,
      verifiedSecondaryChannelToken,
      verifyTransportData.otherData.email,
      verifyTransportData.text,
      setIsEmailVerified,
      setAccountInfo,
      mobileNumber,
      setIsMobileNumberVerified,
      setIsVerifying,
    ]
  );

  useEffect(() => {
    if (location.state) {
      setVerifyTransportData({
        transport: location.state?.transport || "",
        text: location.state?.text || "",
        verificationText: (
          <div className="mb-0">
            {"We have sent a verification code to: "}
            <strong className="text-pns">
              {mobileNumber
                ? replaceCountryCodeInMobileNumber(mobileNumber, 2, "0")
                : "~ unknown"}
            </strong>
          </div>
        ),
        otherData: location.state?.otherData || {},
      });
      setUsedIn(
        location.state?.transport === VALID_TRANSPORT_METHODS.EMAIL
          ? VERIFICATION_USED_IN.SECONDARY_CHANNEL_VERIFY
          : VERIFICATION_USED_IN.ACCOUNT_CREATE_VERIFY
      );
    } else {
      setVerifyTransportData({
        transport: transport || "",
        text: text || "",
        verificationText: verificationText || null,
        otherData: otherData || {},
      });
      setUsedIn(
        transport === VALID_TRANSPORT_METHODS.EMAIL
          ? VERIFICATION_USED_IN.SECONDARY_CHANNEL_VERIFY
          : VERIFICATION_USED_IN.ACCOUNT_CREATE_VERIFY
      );
    }
  }, [
    mobileNumber,
    otherData,
    text,
    transport,
    verificationText,
    location.state,
  ]);

  useEffect(() => {
    if (referenceToken) {
      localStorage.setItem("accountVerifyToken", referenceToken);
    } else {
      const storedToken = localStorage.getItem("accountVerifyToken");
      if (storedToken) {
        // eslint-disable-next-line
        setMobileNumberSendOtpResponse(storedToken);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceToken]);

  // * To show a warning if the user is navigating out of the page or is going to reload the page.
  const handleBeforeUnload = (e) => {
    e.preventDefault();
    const message =
      "Are you sure you want to leave? This will cancel the current verification process.";
    e.returnValue = message;
    return message;
  };

  // * To show a warning if the user is navigating out of the page or is going to reload the page.
  useEffect(() => {
    if (isVerficationProcessOnGoing) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isVerficationProcessOnGoing]);

  return (
    <Row className="my-5 justify-content-center">
      <Col xs={10} sm={10} md={10} lg={8} xl={8}>
        {isVerifying ? (
          <PnSLoadingSpinner
            className="my-3 d-flex flex-column align-items-center"
            text={`Verifying${
              verifyTransportData.text ? " " + verifyTransportData.text : ""
            }...`}
          />
        ) : (
          <Form onSubmit={onSubmit}>
            <div className="verification text-center">
              {verifyTransportData.verificationText}
              <Row className="justify-content-center">
                <Col xs={12} sm={10} md={9} lg={9} xl={8} xxl={6}>
                  <VerificationInput
                    usedInSignup={usedInSignup}
                    usedIn={usedIn}
                    codeItem={codeItem}
                    isRequestingVerifyToken={isResedingOtp}
                    disableResend={disableResend}
                    setCodeItem={setCodeItem}
                    onVerifyAccount={onSubmit}
                    onResendCode={onResendCode}
                    setIsChildLoading={setIsChildLoading}
                  />
                </Col>
              </Row>
              {verifyTransportData.otherData?.skipVerificationIsPossible ? (
                <Button
                  className="mt-3 p-0"
                  variant="link"
                  size="sm"
                  disabled={isVerifying || isResedingOtp}
                  onClick={
                    verifyTransportData.otherData?.onSkip ||
                    onMissingFunctionRef
                  }
                >
                  Skip
                </Button>
              ) : null}
            </div>
          </Form>
        )}
      </Col>
    </Row>
  );
};

Verification.defaultProps = {
  usedInSignup: false,
  mobileNumber: "",
  isVerficationProcessOnGoing: false,
  referenceToken: "",
  accountVerifyToken: null,
  disableResend: false,
  emailReferenceToken: "",
  transport: "",
  text: "",
  verificationText: null,
  otherData: {},
  verifiedSecondaryChannelToken: "",
  verifiedEmail: "",
  setIsChildLoading: () => {},
  setMobileNumberSendOtpResponse: () => {},
  setAccountVerifyToken: () => {},
  setEmailSendOtpResponse: () => {},
  setIsMobileNumberVerified: () => {},
  setIsEmailVerified: () => {},
  callbackAfterSuccessfulVerify: onMissingFunctionRef,
  setIsResedingOtp: () => {},
};

Verification.propTypes = {
  usedInSignup: PropTypes.bool,
  mobileNumber: PropTypes.string,
  isVerficationProcessOnGoing: PropTypes.bool,
  referenceToken: PropTypes.string,
  accountVerifyToken: PropTypes.string,
  disableResend: PropTypes.bool,
  emailReferenceToken: PropTypes.string,
  transport: PropTypes.string,
  text: PropTypes.string,
  verificationText: PropTypes.any,
  otherData: PropTypes.object,
  verifiedSecondaryChannelToken: PropTypes.string,
  verifiedEmail: PropTypes.string,
  setIsChildLoading: PropTypes.func,
  setMobileNumberSendOtpResponse: PropTypes.func,
  setAccountVerifyToken: PropTypes.func,
  setEmailSendOtpResponse: PropTypes.func,
  setIsMobileNumberVerified: PropTypes.func,
  setIsEmailVerified: PropTypes.func,
  callbackAfterSuccessfulVerify: PropTypes.func,
  setIsResedingOtp: PropTypes.func,
};

export default Verification;
